import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleLogo } from '@/components/brand/GoogleLogo';
import { z } from 'zod';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { safeInternalPath } from '@/lib/safe-internal-path';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EyeIcon, EyeOffIcon, LogInIcon, InfoIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLogin } from '@/hooks/use-auth';
import { ROLES } from '@/constants/roles';
import { useOnboardingNavigation } from '@/hooks/use-onboarding-navigation';
import { ApiError } from '@/lib/api-client';
import { logAuthStage } from '@/lib/auth-log';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const formSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Shown when the user was redirected here by an expired session (see
  // api-client redirectToLogin) so they get an explanation instead of a
  // silent bounce.
  const sessionExpired = searchParams.get('session') === 'expired';
  const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)
    ?.from;
  const returnTo = safeInternalPath(
    searchParams.get('from') ??
      (fromState?.pathname ? `${fromState.pathname}${fromState.search ?? ''}` : null),
    '/dashboard',
  );
  const { toast } = useToast();
  const { navigateToOnboarding } = useOnboardingNavigation();

  // Login mutation
  const loginMutation = useLogin();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  // Clear error when user starts typing
  const clearError = () => {
    if (loginError) {
      setLoginError(null);
    }
  };
  const onSubmit = async (data: FormValues) => {
    try {
      logAuthStage('login-submit');

      // Clear any previous errors
      setLoginError(null);

      // Call the login API using TanStack Query mutation
      const response = await loginMutation.mutateAsync(data);

      toast({
        title: 'Login Successful!',
        description: 'Welcome back to LarkPM.',
        variant: 'success',
      });

      // Check onboarding status and redirect accordingly
      if (response.user?.organizationId) {
        // Determine account type from user roles
        const userRoles = response.user?.roles || [];
        let accountType: string | undefined;

        if (userRoles.includes(ROLES.ARCHITECT.slug)) {
          accountType = 'architect';
        } else if (userRoles.includes(ROLES.SUBCONTRACTOR.slug)) {
          accountType = 'subcontractor';
        }
        // If no specific role, default to company onboarding

        const redirectResult = await navigateToOnboarding(
          response.user.organizationId,
          accountType
        );
        if (redirectResult) {
          navigate(redirectResult);
        } else {
          // Restore the pre-expiry path when it is an in-app URL.
          navigate(returnTo);
        }
        logAuthStage('login-navigate', {
          onboarding: Boolean(redirectResult),
          returnTo,
        });
      }
    } catch (error: any) {
      const status = error instanceof ApiError ? error.status : undefined;
      logAuthStage('login-error', { status, message: error?.message });

      // Surface the real reason instead of a blanket "invalid credentials":
      // 403 = unverified email, 429 = rate-limited, others = backend message.
      // Only a true 401 is a credentials problem.
      let message = 'Invalid email or password. Please try again.';
      if (status === 403) {
        message = error.message || 'Please verify your email before logging in.';
      } else if (status === 429) {
        message = 'Too many attempts. Please wait a moment and try again.';
      } else if (status && status !== 401) {
        message = error.message || message;
      }

      // Single, consistent surface (inline alert). Avoid also firing a toast,
      // which previously contradicted the inline message.
      setLoginError(message);
    }
  };

  const handleGoogleSignIn = () => {
    toast({
      title: 'Google Sign-In',
      description: 'Google authentication would be implemented here in future.',
      variant: 'warning',
    });
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleSignUp = () => {
    navigate('/sign-up');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-4xl">
        <Card className=" rounded-2xl overflow-hidden">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 min-h-[600px]">
              {/* Left side - Image */}
              <div className="relative p-6">
                <img
                  src="/login.jpg"
                  alt="Login"
                  className="w-full h-full object-cover rounded-xl scale-x-[-1]"
                />
              </div>

              {/* Right side - Login Form */}
              <div className="px-8 py-12 flex flex-col justify-center">
                {/* Logo */}
                <div className="mb-6">
                  <img
                    src="/logo.png"
                    alt="LarkPM Logo"
                    className="h-10 w-auto object-contain"
                  />
                </div>

                {/* Header */}
                <div className="mb-6">
                  <h1 className="text-xl font-bold tracking-tight text-foreground mb-1">
                    Welcome Back 👋
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Everything you need for precision deck planning is just a
                    login away.
                  </p>
                </div>

                <Form {...form}>
                  {/* Session-expired notice (redirected here by api-client) */}
                  {sessionExpired && !loginError && (
                    <Alert className="mb-4 bg-amber-50 border-amber-200">
                      <InfoIcon className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-800">
                        Session expired
                      </AlertTitle>
                      <AlertDescription className="text-amber-700">
                        Your session expired or your password was changed. Please
                        sign in again.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Error Alert */}
                  {loginError && (
                    <Alert className="mb-4 bg-red-50 border-red-200">
                      <InfoIcon className="h-4 w-4 text-red-600" />
                      <AlertTitle className="text-red-800">
                        Login failed
                      </AlertTitle>
                      <AlertDescription className="text-red-700">
                        {loginError}
                      </AlertDescription>
                    </Alert>
                  )}

                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    {/* Email */}
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Email<span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="Enter your email"
                              {...field}
                              className="mt-1"
                              onChange={(e) => {
                                field.onChange(e);
                                clearError();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Password */}
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-medium">
                            Password<span className="text-red-600">*</span>
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                {...field}
                                className="pr-10"
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={
                                  showPassword
                                    ? 'Hide password'
                                    : 'Show password'
                                }
                              >
                                {showPassword ? (
                                  <EyeOffIcon className="h-4 w-4" />
                                ) : (
                                  <EyeIcon className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Remember Me and Forgot Password */}
                    <div className="flex items-center justify-between">
                      <FormField
                        control={form.control}
                        name="rememberMe"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center space-x-2">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <FormLabel className="text-sm text-foreground">
                                Remember me
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="link"
                        onClick={handleForgotPassword}
                        className="text-foreground font-medium p-0 h-auto text-sm"
                      >
                        Forgot password?
                      </Button>
                    </div>

                    {/* Login Button */}
                    <div className="mt-6">
                      <Button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 h-11"
                        disabled={loginMutation.isPending}
                      >
                        {loginMutation.isPending ? (
                          <span className="flex items-center">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Logging in...
                          </span>
                        ) : (
                          <span className="flex items-center">
                            Login
                            <LogInIcon className="h-4 w-4 ml-2" />
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>

                {/* Sign Up Link */}
                <div className="mt-3 text-center text-sm">
                  <span className="text-muted-foreground">Not a member? </span>
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleSignUp}
                    className="text-foreground font-medium p-0 h-auto text-sm"
                  >
                    sign up now!
                  </Button>
                </div>

                {/* Divider */}
                <div className="flex items-center my-4">
                  <Separator className="flex-1" />
                  <span className="px-4 text-muted-foreground">Or</span>
                  <Separator className="flex-1" />
                </div>

                {/* Google Sign In Button */}
                <div className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleSignIn}
                    className="w-full flex items-center justify-center gap-3 bg-card hover:bg-muted border-border"
                  >
                    <span className="flex items-center gap-3">
                      <GoogleLogo size={20} />
                      Sign in with Google
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
